import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { transformProjectType } from '@atlassian/jira-issue-create-commons';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import type { WatchersConfig } from '@atlassian/jira-issue-create-watchers';
import { ModalHeader as MinimizableModalHeader } from '@atlassian/jira-minimizable-modal';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsGICPositioningEnabled } from '../../../../../controllers/gic-positioning-enabled';
// eslint-disable-next-line jira/import/no-parent-imports
import Watchers from '../../../create-form/actions-container/watchers'; // This would be cleaned up together with gic positioning
import { IMPORT_ISSUES_URL } from './constants';
import HeaderDropdown from './dropdown-menu';
import messages from './messages';
import { SpotlightContainer } from './spotlight-container';

type Props = {
	shouldShowChangerSpotlight?: boolean;
};

export const NewHeader = ({ shouldShowChangerSpotlight }: Props) => {
	const { formatMessage } = useIntl();
	const [{ title, isSubTaskCreationOpen, isCreateLinkedIssue }] = useTriggerIssueCreateModal();

	let titleMsg = expVal('issue-terminology-refresh-m1-fade', 'isIssueTermFaded', false)
		? messages.title
		: messages.titleToBeFaded;
	if (isSubTaskCreationOpen) {
		titleMsg = messages.subtaskTitle;
	} else if (isCreateLinkedIssue) {
		titleMsg = messages.createLinkedIssueDialogTitle;
	}

	const shouldShowChangerSpotlightBoolean = !!shouldShowChangerSpotlight;
	const ViewChangerContainer = shouldShowChangerSpotlightBoolean ? SpotlightContainer : undefined;

	return (
		// eslint-disable-next-line jira/react/no-empty-divs
		<div>
			<MinimizableModalHeader ViewChangerContainer={ViewChangerContainer}>
				{title !== null && title !== '' ? title : formatMessage(titleMsg)}
			</MinimizableModalHeader>
		</div>
	);
};

export type PropsOld = {
	shouldShowChangerSpotlight?: boolean;
	shouldDisableActions: boolean;
	watchersConfigurations?: WatchersConfig;
};

const OldHeader = ({ shouldDisableActions, watchersConfigurations }: PropsOld) => {
	const [{ selectedProject }] = useFormContext();
	const { formatMessage } = useIntl();
	const [{ title, isSubTaskCreationOpen, isCreateLinkedIssue }] = useTriggerIssueCreateModal();

	// Delete callback when cleaning up gic-remove-import-issues-button
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onImportIssues = (_: any, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'importIssues', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const shouldDisableHeaderDropdown = (): boolean =>
		Boolean(shouldDisableActions || selectedProject === null);

	let titleMsg = expVal('issue-terminology-refresh-m1-fade', 'isIssueTermFaded', false)
		? messages.title
		: messages.titleToBeFaded;
	if (isSubTaskCreationOpen) {
		titleMsg = messages.subtaskTitle;
	} else if (isCreateLinkedIssue) {
		titleMsg = messages.createLinkedIssueDialogTitle;
	}

	return (
		// eslint-disable-next-line jira/react/no-empty-divs
		<div>
			<ModalHeader>
				{/* eslint-disable-next-line jira/react/no-empty-divs */}
				<div>
					<ModalTitle>
						{title !== null && title !== '' ? title : formatMessage(titleMsg)}
					</ModalTitle>
				</div>

				<ActionsContainer>
					<Watchers configs={watchersConfigurations} />
					{ff('gic-remove-import-issues-button') ? null : (
						<Button
							href={IMPORT_ISSUES_URL}
							testId="issue-create.ui.modal.modal-wrapper.minimizable-modal-modal.header.import-issues-button"
							onClick={onImportIssues}
							isDisabled={shouldDisableActions}
						>
							{formatMessage(messages.importIssues)}
						</Button>
					)}
					<Box xcss={dropdownContainerStyles}>
						<HeaderDropdown isDisabled={shouldDisableHeaderDropdown()} />
					</Box>
				</ActionsContainer>
			</ModalHeader>
		</div>
	);
};

export const Header = (props: PropsOld) => {
	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	if (isGICPositioningEnabled()) {
		return <NewHeader shouldShowChangerSpotlight={props.shouldShowChangerSpotlight} />;
	}
	return <OldHeader {...props} />;
};

export default Header;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div<{
	children?: ReactNode;
}>({
	marginLeft: 'auto',
	display: 'flex',
});

const dropdownContainerStyles = xcss({
	marginLeft: 'space.100',
});
